import { useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export function CalendarView({ services, onSelect }) {
  const [cursor, setCursor] = useState(new Date());

  const monthStart = startOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const byDay = (day) =>
    services.filter((s) =>
      (s.dates && s.dates.length ? s.dates : [s.date_time]).some((d) => {
        try {
          return isSameDay(parseISO(d), day);
        } catch {
          return false;
        }
      })
    );

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-semibold capitalize">
          {format(cursor, "MMMM yyyy", { locale: it })}
        </h3>
        <div className="flex gap-1">
          <Button size="icon" variant="outline" data-testid="calendar-prev" onClick={() => setCursor(subMonths(cursor, 1))} className="rounded-full h-9 w-9">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" data-testid="calendar-today" onClick={() => setCursor(new Date())}>Oggi</Button>
          <Button size="icon" variant="outline" data-testid="calendar-next" onClick={() => setCursor(addMonths(cursor, 1))} className="rounded-full h-9 w-9">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-muted-foreground py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const items = byDay(day);
          const inMonth = isSameMonth(day, cursor);
          const today = isSameDay(day, new Date());
          return (
            <div
              key={day.toISOString()}
              className={`min-h-[92px] rounded-lg border p-1.5 flex flex-col gap-1 ${
                inMonth ? "bg-background border-border" : "bg-muted/40 border-transparent"
              }`}
            >
              <span className={`text-xs font-semibold ${today ? "bg-[#D32F2F] text-white rounded-full h-5 w-5 flex items-center justify-center" : inMonth ? "text-foreground" : "text-muted-foreground/50"}`}>
                {format(day, "d")}
              </span>
              <div className="flex flex-col gap-1 overflow-hidden">
                {items.slice(0, 3).map((s) => (
                  <button
                    key={s.id}
                    data-testid={`calendar-event-${s.id}`}
                    onClick={() => onSelect?.(s)}
                    className={`text-left text-[11px] leading-tight truncate rounded px-1.5 py-0.5 border ${
                      s.status === "coperto"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                    title={s.event_name}
                  >
                    {s.event_name}
                  </button>
                ))}
                {items.length > 3 && <span className="text-[10px] text-muted-foreground px-1">+{items.length - 3} altri</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
